import React, { useCallback, useMemo } from 'react'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import TextLink from 'LuxKit/TextLink'
import { useIntl } from 'react-intl'
import { getSaleUnitPluralNoun } from 'hooks/useSaleUnitPlural'

type AccommodationRoomTileKnownMessage =
  'loading' |
  'members-exclusive' |
  'sold-out-on-dates' |
  'sold-out' |
  'exceeded-capacity' |
  'sold-out-on-dates-or-exceeded-capacity' |
  'available-on-dates' |
  'available-on-dates-variant' |
  'available-for-some-rooms'

interface Props {
  message: AccommodationRoomTileKnownMessage
  offer: App.HotelOffer | App.BedbankOffer
  onMultiRoomModeClick?: () => void
  onChangeOccupancyClick?: () => void
  onChangeDatesClick?: () => void
}

function AccommodationRoomTileMessage(props: Props) {
  const {
    message,
    offer,
    onMultiRoomModeClick,
    onChangeOccupancyClick,
    onChangeDatesClick,
  } = props

  const intl = useIntl()

  const renderTryDifferentDatesLink = useCallback((chunks: React.ReactNode) => {
    if (!onChangeDatesClick) return <>{chunks}</>
    return <TextLink onClick={onChangeDatesClick}>{chunks}</TextLink>
  }, [onChangeDatesClick])

  const renderAddMoreRoomsLink = useCallback((chunks: React.ReactNode) => {
    if (!onChangeOccupancyClick) return <>{chunks}</>
    return <TextLink onClick={onChangeOccupancyClick}>{chunks}</TextLink>
  }, [onChangeOccupancyClick])

  const renderChooseIndividuallyLink = useCallback((chunks: React.ReactNode) => {
    if (!onMultiRoomModeClick) return <>{chunks}</>
    return <TextLink onClick={onMultiRoomModeClick}>{chunks}</TextLink>
  }, [onMultiRoomModeClick])

  const messageLabel = useMemo(() => {
    switch (message) {
      case 'members-exclusive':
        return intl.formatMessage({
          id: '5oe3T8UMAn',
          defaultMessage: 'Sign up to access exclusive rates',
          description: 'Message shown when rates are walled behind membership signup.',
        })
      case 'sold-out-on-dates':
        return onChangeDatesClick
          ? intl.formatMessage(
              {
                id: 'JD/2Mwx3e0',
                defaultMessage: 'This {saleUnitLong} is sold out on your dates. Choose another {saleUnit} or <link>try different dates</link>.',
                description: 'Message shown when the selected room/package is sold out on chosen dates, with a link to change dates.',
              },
              {
                saleUnitLong: offer.saleUnitLong,
                saleUnit: offer.saleUnit,
                link: renderTryDifferentDatesLink,
              },
            )
          : intl.formatMessage(
              {
                id: '5KagClSaIa',
                defaultMessage: 'This {saleUnitLong} is sold out on your dates',
                description: 'Message shown when the selected room/package is sold out on chosen dates (no change dates action available).',
              },
              {
                saleUnitLong: offer.saleUnitLong,
              },
            )
      case 'sold-out':
        return intl.formatMessage(
          {
            id: 'jgJjOJPEDp',
            defaultMessage: 'This {saleUnitLong} is sold out',
            description: 'Message shown when the selected room/package is sold out.',
          },
          {
            saleUnitLong: offer.saleUnitLong,
          },
        )
      case 'exceeded-capacity':
        return onChangeOccupancyClick
          ? intl.formatMessage(
              {
                id: 'dMTQFD09FN',
                defaultMessage: 'This {saleUnit} cannot accommodate your number of guests. Choose another {saleUnit} or <link>add more {saleUnitPlural}</link>.',
                description: 'Message shown when the selected room/package exceeds guest capacity, with a link to add more rooms/units.',
              },
              {
                saleUnit: offer.saleUnit,
                saleUnitPlural: getSaleUnitPluralNoun(offer.saleUnit),
                link: renderAddMoreRoomsLink,
              },
            )
          : intl.formatMessage(
              {
                id: 'C0aQDqlM4l',
                defaultMessage: 'This {saleUnit} cannot accommodate your number of guests',
                description: 'Message shown when the selected room/package exceeds guest capacity (no change occupancy action available).',
              },
              {
                saleUnit: offer.saleUnit,
              },
            )
      case 'sold-out-on-dates-or-exceeded-capacity':
        if (onChangeDatesClick && onChangeOccupancyClick) {
          return intl.formatMessage(
            {
              id: 'L2JpgaI2Zt',
              defaultMessage: 'This {saleUnit} is sold out on your dates or cannot accommodate your number of guests. Choose another {saleUnit}, <datesLink>try different dates</datesLink> or <occupancyLink>add more {saleUnitPlural}</occupancyLink>.',
              description: 'Message shown when room is either sold out on dates or exceeds capacity, with links to change dates or add more rooms.',
            },
            {
              saleUnit: offer.saleUnit,
              saleUnitPlural: getSaleUnitPluralNoun(offer.saleUnit),
              datesLink: renderTryDifferentDatesLink,
              occupancyLink: renderAddMoreRoomsLink,
            },
          )
        }
        if (onChangeDatesClick) {
          return intl.formatMessage(
            {
              id: 'pntDxcVM3G',
              defaultMessage: 'This {saleUnit} is sold out on your dates or cannot accommodate your number of guests. Choose another {saleUnit} or <datesLink>try different dates</datesLink>.',
              description: 'Message shown when room is either sold out on dates or exceeds capacity, with a link to change dates.',
            },
            {
              saleUnit: offer.saleUnit,
              datesLink: renderTryDifferentDatesLink,
            },
          )
        }
        if (onChangeOccupancyClick) {
          return intl.formatMessage(
            {
              id: 'Ot5QGtJS36',
              defaultMessage: 'This {saleUnit} is sold out on your dates or cannot accommodate your number of guests. Choose another {saleUnit} or <occupancyLink>add more {saleUnitPlural}</occupancyLink>.',
              description: 'Message shown when room is either sold out on dates or exceeds capacity, with a link to add more rooms/units.',
            },
            {
              saleUnit: offer.saleUnit,
              saleUnitPlural: getSaleUnitPluralNoun(offer.saleUnit),
              occupancyLink: renderAddMoreRoomsLink,
            },
          )
        }
        return intl.formatMessage(
          {
            id: 'VF7Qobz6HD',
            defaultMessage: 'This {saleUnit} is sold out on your dates or cannot accommodate your number of guests.',
            description: 'Message shown when room is either sold out on dates or exceeds capacity (no actions available).',
          },
          {
            saleUnit: offer.saleUnit,
          },
        )
      case 'available-for-some-rooms':
        return onMultiRoomModeClick
          ? intl.formatMessage(
              {
                id: 'knJy06fsAf',
                defaultMessage: 'This {saleUnit} type works for some {saleUnitPlural} in your group, but not all. To select it, try <link>choosing {saleUnitPlural} individually</link>.',
                description: 'Message shown when a room type only works for some rooms in the group, with a link to switch to individual room selection.',
              },
              {
                saleUnit: offer.saleUnit,
                saleUnitPlural: getSaleUnitPluralNoun(offer.saleUnit),
                link: renderChooseIndividuallyLink,
              },
            )
          : intl.formatMessage(
              {
                id: '24fQKyEolo',
                defaultMessage: 'This {saleUnit} type works for some {saleUnitPlural} in your group, but not all. To select it, try choosing {saleUnitPlural} individually.',
                description: 'Message shown when a room type only works for some rooms in the group (no action available).',
              },
              {
                saleUnit: offer.saleUnit,
                saleUnitPlural: getSaleUnitPluralNoun(offer.saleUnit),
              },
            )
    }
  }, [message, intl, offer, onChangeDatesClick, onChangeOccupancyClick, onMultiRoomModeClick, renderTryDifferentDatesLink, renderAddMoreRoomsLink, renderChooseIndividuallyLink])

  if (!messageLabel) return null

  return <MessageBanner kind="informational-primary" density="compact">
    {messageLabel}
  </MessageBanner>
}

export default withOfferPageAndReduxState((offerPageState) => ({
  offer: (offerPageState.offer ?? offerPageState.bedbankOffer)!,
}), AccommodationRoomTileMessage)
